import React, { useMemo, useState, Fragment } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { useAuth0 } from '@auth0/auth0-react';
import StandardPage from '../../components/StandardPage';
import { useCurrentPlan, useOrganization } from '../../context/organization';
import { toast } from 'react-toastify';
import Loading from '../../components/Loading';
import { usePricingPlans } from '../../context/pricing-plans';
import { Dialog, Transition } from '@headlessui/react';
import { handleApiError } from '../../api-client/error_handler';
import axios from 'axios';

const USAGE_FREE_PLAN_ID = '9t4n5p';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY || '');

const PricingPlans: React.FC = () => {
  const { pricingPlans, loading } = usePricingPlans();
  const [btnLoading, setBtnLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const { organization } = useOrganization();
  const { currentPlan, setCurrentPlan } = useCurrentPlan();
  const { getAccessTokenSilently } = useAuth0();

  const fmtEUR = useMemo(
    () => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }),
    []
  );

  const featuredSlug = useMemo(() => {
    if (!pricingPlans?.length) return '';
    const paid = pricingPlans.filter(p => p.cost > 0).sort((a, b) => a.cost - b.cost);
    return paid[Math.min(1, paid.length - 1)]?.slug || pricingPlans[0].slug;
  }, [pricingPlans]);

  if (organization == null || currentPlan == null) return null;

  const handleCheckout = async (planId: string) => {
    try {
      setIsModalOpen(false);
      setSelectedPlanId(planId);
      setBtnLoading(true);

      const token = await getAccessTokenSilently();
      const { data } = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/stripe/create-checkout-session`,
        { pricing_plan_id: planId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const stripe = await stripePromise;
      if (!stripe) throw new Error('Stripe failed to load. Please try again.');
      const { error } = await stripe.redirectToCheckout({ sessionId: data.session_id });
      if (error) throw error;
    } catch (error: unknown) {
      toast.error(handleApiError(error));
    } finally {
      setBtnLoading(false);
    }
  };

  const openConfirmForPlan = (planId: string) => {
    setSelectedPlanId(planId);
    setIsModalOpen(true);
  };

  const handleResetPlan = async () => {
    setIsModalOpen(false);
    setBtnLoading(true);
    const previousPlan = currentPlan;
    try {
      const token = await getAccessTokenSilently();
      const { data } = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/organization/cancel-plan`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCurrentPlan(data.current_plan);
      toast.success('Your plan was updated.');
    } catch (error: unknown) {
      setCurrentPlan(previousPlan);
      toast.error(handleApiError(error));
    } finally {
      setBtnLoading(false);
    }
  };

  const handleContactUs = () => {
    window.location.href = 'mailto:sales@civapi.com?subject=Custom Plan Inquiry';
  };

  const renderSpec = (spec: [string, any], index: number, nested = false) => {
    if (typeof spec[1] === 'string' || typeof spec[1] === 'number' || typeof spec[1] === 'boolean') {
      return (
        <li key={`${spec[0]}-${index}`} className={`flex items-start gap-2 ${nested ? 'mt-3' : ''}`}>
          <svg className="mt-0.5 h-5 w-5 flex-shrink-0 text-emerald-500" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 01.0 1.414l-8 8a1 1 0 01-1.414 0l-4-4A1 1 0 014.707 9.293L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
          </svg>
          <span className="text-sm text-slate-700">
            {isNaN(Number(spec[0])) ? <><span className="font-medium">{spec[0]}:</span> {String(spec[1])}</> : String(spec[1])}
          </span>
        </li>
      );
    }
    return (
      <li key={`${spec[0]}-${index}`}>
        <div className="text-sm font-medium text-slate-800 mt-2">{spec[0]}:</div>
        <ul className="ml-1">
          {Object.entries(spec[1]).map((s, i) => renderSpec(s as [string, any], i, true))}
        </ul>
      </li>
    );
  };

  const isCurrent = (slug: string) => slug === currentPlan.slug;

  return (
    <StandardPage title="Manage Plan">
      {loading ? (
        <Loading />
      ) : (
        <>
          {/* Confirm modal (upgrade/downgrade/free) */}
          <Transition show={isModalOpen} as={Fragment}>
            <Dialog onClose={() => !btnLoading && setIsModalOpen(false)} className="relative z-[9999]">
              <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0">
                <div className="fixed inset-0 bg-black/40" />
              </Transition.Child>
              <div className="fixed inset-0 overflow-y-auto">
                <div className="flex min-h-full items-end justify-center p-4 sm:items-center sm:p-0">
                  <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enterTo="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-150" leaveFrom="opacity-100 translate-y-0 sm:scale-100" leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                    <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-2xl ring-1 ring-slate-100">
                      <Dialog.Title className="text-lg font-semibold text-slate-900">Confirm change</Dialog.Title>
                      <p className="mt-2 text-sm text-slate-600">
                        Are you sure you want to {selectedPlanId === USAGE_FREE_PLAN_ID ? 'switch to the Free plan' : 'change your plan'}?
                        This will update your subscription.
                      </p>
                      <div className="mt-4 flex justify-end gap-2">
                        <button
                          onClick={() => setIsModalOpen(false)}
                          disabled={btnLoading}
                          className="rounded-full bg-slate-200 px-4 py-2 text-sm font-semibold text-slate-800 hover:bg-slate-300 disabled:opacity-50"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={() => {
                            if (!selectedPlanId) return;
                            if (selectedPlanId === USAGE_FREE_PLAN_ID) handleResetPlan();
                            else handleCheckout(selectedPlanId);
                          }}
                          disabled={btnLoading}
                          className="rounded-full bg-blue-600 px-5 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
                        >
                          {btnLoading ? 'Processing…' : 'Confirm'}
                        </button>
                      </div>
                    </Dialog.Panel>
                  </Transition.Child>
                </div>
              </div>
            </Dialog>
          </Transition>

          <section className="bg-slate-50">
            <div className="mx-auto max-w-screen-2xl px-6 py-8">
              <div className="mb-6 flex items-end justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-slate-900">Choose your plan</h2>
                  <p className="text-sm text-slate-600">Your current plan: <span className="font-semibold">{currentPlan.name}</span></p>
                </div>
                <button
                  className="rounded-full bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-emerald-700"
                  onClick={() => window.open('mailto:sales@civapi.com?subject=Custom Plan Inquiry', '_self')}
                >
                  Talk to Sales
                </button>
              </div>

              <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
                {pricingPlans.map(plan => {
                  const current = isCurrent(plan.slug);
                  const featured = plan.slug === featuredSlug;
                  const isUpgrade = currentPlan.cost < plan.cost;
                  const isDowngradeOrFree = currentPlan.cost > plan.cost || plan.cost === 0;

                  return (
                    <div
                      key={plan.id}
                      className={[
                        'flex flex-col justify-between rounded-2xl border bg-white p-7 shadow-sm transition hover:shadow-md',
                        featured ? 'border-blue-200 ring-1 ring-blue-100' : 'border-slate-200'
                      ].join(' ')}
                    >
                      <div>
                        <div className="mb-4 flex items-center justify-between">
                          <h3 className="text-2xl font-bold text-slate-900">{plan.name}</h3>
                          {current && <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">Current</span>}
                          {!current && featured && <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700">Popular</span>}
                        </div>

                        <div className="my-6 flex items-baseline">
                          <span className="text-5xl font-extrabold text-slate-900">
                            {plan.cost > 0 ? fmtEUR.format(plan.cost) : 'Free'}
                          </span>
                          {plan.cost > 0 && <span className="ml-2 text-sm text-slate-500">/month</span>}
                        </div>

                        {plan.text && <p className="mb-4 text-sm text-slate-600">{plan.text}</p>}

                        <ul className="mb-6 space-y-3">
                          {Object.entries(plan.specs)
                            .sort((a, b) => (typeof b[1] === 'string' ? 1 : -1))
                            .map((spec, i) => renderSpec(spec as [string, any], i))}
                        </ul>
                      </div>

                      <button
                        disabled={current || btnLoading}
                        onClick={() => openConfirmForPlan(plan.id)}
                        className={[
                          'w-full rounded-lg px-5 py-3 font-semibold transition',
                          current
                            ? 'cursor-not-allowed bg-slate-200 text-slate-600'
                            : isUpgrade
                            ? 'bg-blue-600 text-white hover:bg-blue-700'
                            : 'bg-orange-600 text-white hover:bg-orange-700'
                        ].join(' ')}
                        aria-label={current ? 'Current plan' : isUpgrade ? 'Upgrade' : 'Downgrade'}
                      >
                        {btnLoading && selectedPlanId === plan.id ? (
                          <span className="inline-flex items-center">
                            <svg className="mr-2 h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="4" />
                              <path d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="4" />
                            </svg>
                            Processing…
                          </span>
                        ) : current ? (
                          'Current'
                        ) : isUpgrade ? (
                          'Upgrade'
                        ) : (
                          'Downgrade'
                        )}
                      </button>
                    </div>
                  );
                })}
              </div>

              <div className="mt-10 flex flex-col items-center">
                <p className="mb-2 text-center text-lg font-medium text-slate-800">Need more Data or API Calls?</p>
                <button
                  className="w-full max-w-xs rounded-lg bg-emerald-600 px-6 py-3 font-bold text-white hover:bg-emerald-700"
                  onClick={handleContactUs}
                >
                  Contact Us
                </button>
                <p className="mt-2 text-center text-sm text-slate-600">
                  Reach out to&nbsp;
                  <a href="mailto:sales@civapi.com?subject=Custom Plan Inquiry" className="text-blue-600 hover:underline">
                    sales@civapi.com
                  </a>
                </p>
              </div>
            </div>
          </section>
        </>
      )}
    </StandardPage>
  );
};

export default PricingPlans;
